import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Users, ChevronRight, Filter, Plus, Search, MessageSquare, ArrowRightCircle, Archive, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const STAGE_META = {
  new: { label: 'Nieuw', color: '#64748b', bg: '#f1f5f9' },
  contacted: { label: 'Contact gelegd', color: '#0284c7', bg: '#e0f2fe' },
  scan_completed: { label: 'Scan voltooid', color: '#7c3aed', bg: '#f3e8ff' },
  interested: { label: 'Interesse', color: '#D4A017', bg: '#fef3c7' },
  follow_up: { label: 'Follow-up', color: '#f97316', bg: '#ffedd5' },
  offer_sent: { label: 'Offerte verstuurd', color: '#0891b2', bg: '#cffafe' },
  won: { label: 'Gewonnen', color: '#16a34a', bg: '#d1fae5' },
  lost: { label: 'Verloren', color: '#dc2626', bg: '#fee2e2' },
};

const STAGE_ORDER = ['new', 'contacted', 'scan_completed', 'interested', 'follow_up', 'offer_sent', 'won', 'lost'];

export default function CrmPipelinePage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [creating, setCreating] = useState(false);
  const [newLead, setNewLead] = useState({ email: '', company_name: '', contact_name: '', phone: '', website: '', source: 'manual', notes: '' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api('get', '/api/admin/crm/pipeline');
      setData(r.data);
    } catch (e) { toast.error('Laden mislukt'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createLead = async () => {
    if (!newLead.email) { toast.error('Email vereist'); return; }
    try {
      await api('post', '/api/admin/crm/leads', newLead);
      toast.success('Lead aangemaakt + ingeschreven in nurture sequence');
      setCreating(false);
      setNewLead({ email: '', company_name: '', contact_name: '', phone: '', website: '', source: 'manual', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Aanmaken mislukt'); }
  };

  const moveStage = async (leadId, newStage) => {
    try {
      await api('post', `/api/admin/crm/leads/${leadId}/move-stage`, { stage: newStage });
      toast.success(`Verplaatst naar ${STAGE_META[newStage]?.label || newStage}`);
      load();
    } catch (e) { toast.error('Verplaatsen mislukt'); }
  };

  const convertLead = async (leadId) => {
    if (!window.confirm('Lead converteren naar klant? Stuurt automatisch een welkomstmail.')) return;
    try {
      const r = await api('post', `/api/admin/crm/leads/${leadId}/convert`, {});
      toast.success(`Klant aangemaakt — mail status: ${r.data.email_status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Converteren mislukt'); }
  };

  const archive = async (leadId) => {
    if (!window.confirm('Lead archiveren?')) return;
    await api('post', `/api/admin/crm/leads/${leadId}/archive`);
    toast.info('Gearchiveerd');
    load();
  };

  if (loading || !data) return <div className="text-center py-20 text-[#94a3b8]">Pipeline laden...</div>;

  const totalLeads = Object.values(data.stats || {}).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="crm-pipeline-page">
      <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0f172a] flex items-center gap-2"><Users size={20} className="text-[#D4A017]" /> CRM Pipeline</h1>
          <p className="text-sm text-[#64748b] mt-1">{totalLeads} actieve leads · {data.overdue_followups || 0} overdue follow-ups</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView(view === 'kanban' ? 'list' : 'kanban')} className="text-xs bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
            {view === 'kanban' ? 'List view' : 'Kanban view'}
          </button>
          <button onClick={load} className="text-xs bg-white border border-slate-200 text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
            <RefreshCw size={12} /> Refresh
          </button>
          <button data-testid="new-lead-btn" onClick={() => setCreating(true)} className="text-xs bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
            <Plus size={12} /> Nieuwe lead
          </button>
        </div>
      </div>

      {/* Kanban view */}
      {view === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 500 }}>
          {STAGE_ORDER.map(stage => {
            const items = (data.by_stage || {})[stage] || [];
            const m = STAGE_META[stage] || {};
            const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
            const onDrop = (e) => {
              e.preventDefault();
              const leadId = e.dataTransfer.getData('text/lead-id');
              const fromStage = e.dataTransfer.getData('text/from-stage');
              if (leadId && fromStage !== stage) {
                moveStage(leadId, stage);
              }
            };
            return (
              <div
                key={stage}
                data-testid={`stage-column-${stage}`}
                onDragOver={onDragOver}
                onDrop={onDrop}
                className="bg-slate-50 rounded-xl flex-shrink-0 w-72 flex flex-col"
              >
                <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: m.color }}>{m.label}</span>
                  <span className="text-[10px] font-bold text-[#94a3b8] bg-white border border-slate-200 px-2 py-0.5 rounded">{items.length}</span>
                </div>
                <div className="p-2 space-y-2 overflow-y-auto flex-1" style={{ minHeight: 400, maxHeight: 600 }}>
                  {items.slice(0, 50).map(lead => (
                    <LeadCard key={lead.id} lead={lead} onClick={() => setSelected(lead)} onMoveStage={moveStage} onConvert={convertLead} onArchive={archive} stage={stage} />
                  ))}
                  {items.length > 50 && <p className="text-[10px] text-center text-[#94a3b8]">+{items.length - 50} meer...</p>}
                  {items.length === 0 && <p className="text-xs text-center text-[#94a3b8] py-4">Sleep een lead hierheen</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {view === 'list' && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-3 border-b border-slate-200">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-2.5 text-[#94a3b8]" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek op email, bedrijf, naam..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg" />
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-[700px] overflow-y-auto">
            {STAGE_ORDER.flatMap(s => data.by_stage[s] || []).filter(l => !search || (l.email + l.company_name + l.contact_name).toLowerCase().includes(search.toLowerCase())).slice(0, 200).map(lead => (
              <div key={lead.id} onClick={() => setSelected(lead)} className="p-3 hover:bg-slate-50 cursor-pointer flex justify-between items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span style={{ background: STAGE_META[lead.stage]?.bg, color: STAGE_META[lead.stage]?.color }} className="text-[10px] font-bold px-2 py-0.5 rounded uppercase">{STAGE_META[lead.stage]?.label || lead.stage}</span>
                    <p className="font-bold text-sm text-[#0f172a]">{lead.company_name || lead.contact_name || lead.email}</p>
                  </div>
                  <p className="text-xs text-[#64748b] mt-1">{lead.email} · {lead.source || 'unknown'}</p>
                </div>
                <ChevronRight size={14} className="text-[#94a3b8]" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl max-w-md w-full p-5">
            <h2 className="font-bold text-lg text-[#0f172a] mb-4">Nieuwe lead</h2>
            <div className="space-y-2">
              {[
                ['email', 'Email *', 'email'],
                ['company_name', 'Bedrijfsnaam *', 'text'],
                ['contact_name', 'Contactpersoon', 'text'],
                ['phone', 'Telefoon', 'text'],
                ['website', 'Website', 'text'],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-[10px] text-[#64748b] uppercase font-bold tracking-wide">{label}</label>
                  <input data-testid={`new-lead-${k}`} type={type} value={newLead[k]} onChange={e => setNewLead({ ...newLead, [k]: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                </div>
              ))}
              <div>
                <label className="text-[10px] text-[#64748b] uppercase font-bold tracking-wide">Notities</label>
                <textarea rows={3} value={newLead.notes} onChange={e => setNewLead({ ...newLead, notes: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <p className="text-[11px] text-[#94a3b8] mt-3">→ Lead wordt automatisch ingeschreven in de 14-daagse nurture sequence.</p>
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setCreating(false)} className="text-sm font-semibold text-[#64748b]">Annuleren</button>
              <button data-testid="save-new-lead-btn" onClick={createLead} className="bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] text-sm font-bold px-5 py-2 rounded-lg">Aanmaken</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selected && <LeadDetailPanel lead={selected} onClose={() => setSelected(null)} api={api} onChanged={load} />}
    </div>
  );
}

const LeadCard = ({ lead, onClick, onMoveStage, onConvert, onArchive, stage }) => {
  const seqStatus = lead.sequence_status || 'not_enrolled';
  const onDragStart = (e) => {
    e.dataTransfer.setData('text/lead-id', lead.id);
    e.dataTransfer.setData('text/from-stage', stage);
    e.dataTransfer.effectAllowed = 'move';
  };
  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="bg-white rounded-lg border border-slate-200 hover:border-[#D4A017] cursor-grab active:cursor-grabbing p-3"
    >
      <p className="font-bold text-sm text-[#0f172a] truncate">{lead.company_name || lead.contact_name || lead.email}</p>
      <p className="text-[11px] text-[#64748b] truncate">{lead.email}</p>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {lead.source && <span className="text-[9px] bg-slate-100 text-[#64748b] px-1.5 py-0.5 rounded uppercase">{lead.source}</span>}
        {seqStatus === 'active' && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded uppercase">In sequence</span>}
        {seqStatus === 'converted' && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase">Converted</span>}
        {seqStatus === 'unsubscribed' && <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded uppercase">Unsub</span>}
      </div>
      {stage !== 'won' && stage !== 'lost' && (
        <div className="flex gap-1 mt-2" onClick={e => e.stopPropagation()}>
          {stage !== 'lost' && (
            <button onClick={() => onConvert(lead.id)} className="text-[10px] flex-1 bg-green-50 hover:bg-green-100 text-green-700 font-bold px-2 py-1 rounded flex items-center justify-center gap-1" title="Converteer naar klant">
              <ArrowRightCircle size={10} /> Win
            </button>
          )}
          <button onClick={() => onMoveStage(lead.id, 'lost')} className="text-[10px] bg-red-50 hover:bg-red-100 text-red-600 font-bold px-2 py-1 rounded" title="Markeer als lost">Lost</button>
          <button onClick={() => onArchive(lead.id)} className="text-[10px] bg-slate-100 hover:bg-slate-200 text-[#64748b] font-bold px-2 py-1 rounded" title="Archiveer"><Archive size={10} /></button>
        </div>
      )}
    </div>
  );
};

const LeadDetailPanel = ({ lead, onClose, api, onChanged }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api('get', `/api/admin/crm/leads/${lead.id}`).then(r => setData(r.data));
  }, [lead.id]);
  const moveStage = async (s) => {
    await api('post', `/api/admin/crm/leads/${lead.id}/move-stage`, { stage: s });
    toast.success(`Verplaatst naar ${STAGE_META[s]?.label}`);
    onChanged();
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="lead-detail-panel" className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs text-[#94a3b8] uppercase font-bold">Lead profiel</p>
              <h2 className="text-lg font-bold text-[#0f172a] mt-1">{lead.company_name || lead.contact_name || lead.email}</h2>
              <p className="text-sm text-[#64748b]">{lead.email}</p>
            </div>
            <span style={{ background: STAGE_META[lead.stage]?.bg, color: STAGE_META[lead.stage]?.color }} className="text-[10px] font-bold px-2 py-0.5 rounded uppercase">{STAGE_META[lead.stage]?.label || lead.stage}</span>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {lead.phone && <div><p className="text-[10px] text-[#94a3b8] uppercase font-bold">Telefoon</p><p>{lead.phone}</p></div>}
            {lead.website && <div><p className="text-[10px] text-[#94a3b8] uppercase font-bold">Website</p><a href={lead.website} target="_blank" rel="noreferrer" className="text-[#D4A017] underline">{lead.website}</a></div>}
            <div><p className="text-[10px] text-[#94a3b8] uppercase font-bold">Source</p><p>{lead.source || '-'}</p></div>
            <div><p className="text-[10px] text-[#94a3b8] uppercase font-bold">Aangemaakt</p><p>{(lead.created_at || '').slice(0, 16).replace('T', ' ')}</p></div>
          </div>

          {/* Sequence status */}
          {data && (
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] text-[#94a3b8] uppercase font-bold mb-2">Email sequence</p>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${data.lead.sequence_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{data.lead.sequence_status || 'not_enrolled'}</span>
                {data.lead.sequence_step !== undefined && <span className="text-xs text-[#64748b]">Stap {data.lead.sequence_step}</span>}
              </div>
              {data.lead.next_send_at && <p className="text-xs text-[#64748b]">Volgende send: {data.lead.next_send_at.slice(0, 16).replace('T', ' ')}</p>}
              {data.sends && data.sends.length > 0 && (
                <details className="mt-2"><summary className="text-xs font-bold text-[#0f172a] cursor-pointer">Send history ({data.sends.length})</summary>
                  <div className="mt-2 space-y-1">
                    {data.sends.map(s => (
                      <div key={s.id} className="text-[11px] flex justify-between bg-white rounded p-2">
                        <span className="text-[#0f172a]">{s.subject}</span>
                        <span className={`uppercase font-bold ${s.status === 'sent' ? 'text-green-700' : 'text-red-700'}`}>{s.status}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Stage change */}
          <div>
            <p className="text-[10px] text-[#94a3b8] uppercase font-bold mb-2">Verplaats stage</p>
            <div className="flex flex-wrap gap-1.5">
              {STAGE_ORDER.map(s => (
                <button key={s} onClick={() => moveStage(s)} disabled={s === lead.stage} className={`text-[10px] font-bold px-2.5 py-1.5 rounded ${s === lead.stage ? 'bg-slate-200 text-[#94a3b8] cursor-not-allowed' : 'bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a]'}`}>{STAGE_META[s].label}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="p-3 border-t border-slate-200 text-right">
          <button onClick={onClose} className="text-sm font-semibold text-[#64748b]">Sluiten</button>
        </div>
      </div>
    </div>
  );
};
